import { Box } from '@mui/material';
import withObservables from '@nozbe/with-observables';
import React from 'react';
import { of } from 'rxjs';
import Database from 'shared/db/services/Database.web';
import User from 'shared/db/services/User';

import { useOrganisationsContext } from '@/context/OrganisationsContext';
import { useSubscriptionContext } from '@/context/SubscriptionContext';
import withSyncContext from '@/hoc/withInitialSyncContext';
import withUserProfileData from '@/hoc/withUserProfileData';
import { databaseValidation } from '@/services/databaseValidation';
import { FirebaseImagesService } from '@/services/firebase/images';
import Logger from '@/services/logger';

import {
    CurrentSubscriptionPlan,
    PremiumPageHeader,
    SubscriptionHeader,
    SubscriptionLegend,
    SubscriptionPlans,
    SubscriptionTeamSeats,
    SwitchTeam,
} from './components';
import { Props } from './types';

const PremiumPage: React.FC<Props> = ({
    userProfile,
    isInitialSyncInProgress,
}) => {
    const user = userProfile?.[0];
    const isTeamCreated = user?.isTeamCreated;

    const { userOrganisation } = useOrganisationsContext();
    const { loading, isCurrentSubscriptionActive } = useSubscriptionContext();
    const isCurrentOrganisationOwner = userOrganisation?.owner;

    if (loading || isInitialSyncInProgress) {
        return <></>;
    }

    if (!isCurrentOrganisationOwner) {
        return <SwitchTeam />;
    }

    if (!isCurrentSubscriptionActive) {
        return (
            <Box>
                <PremiumPageHeader />
                <SubscriptionPlans />
                <SubscriptionLegend />
            </Box>
        );
    }

    return (
        <Box className="flex flex-col gap-5 pb-20">
            <SubscriptionHeader />
            <CurrentSubscriptionPlan />
            <SubscriptionTeamSeats isTeamCreated={isTeamCreated || false} />
        </Box>
    );
};

const enhance = withObservables<Props, unknown>(
    ['isInitialSyncInProgress', 'userProfileData'],
    ({ isInitialSyncInProgress, userProfileData }) => {
        const database = Database.getDatabase();

        const userService = new User({
            database,
            imageService: new FirebaseImagesService(),
            logDBAction: Logger.logRecordActivity,
            validation: databaseValidation,
        });

        return {
            userProfile:
                isInitialSyncInProgress || !userProfileData?.id
                    ? of(undefined)
                    : userService.getById(userProfileData.id),
        };
    },
);

export default withSyncContext<Props>(
    withUserProfileData(enhance(PremiumPage)),
);
